import React from 'react'
import Skeleton from 'components/skeleton'

// The shape of the dashboards MortaAI builds on the page kit, at the kit's spacing (24px around the page, 16px
// between blocks, 16px inside cards), so the page lands where its placeholders were.
const KPI_LABELS = ['w-24', 'w-20', 'w-28', 'w-16']
const KPI_VALUES = ['w-20', 'w-14', 'w-24', 'w-16']
const BAR_HEIGHTS = ['55%', '80%', '40%', '95%', '65%', '30%', '75%', '50%', '85%', '45%']
const TABLE_COLUMNS = ['w-32', 'w-20', 'w-24', 'w-16']
// Enough rows to run past the bottom, where the page fades out as if it went on.
const TABLE_ROWS = 10

/** A card of the skeleton page, as the kit's cards are: the surface colour, bordered, the app's corners. */
const Card = ({ className = '', children }: { className?: string; children: React.ReactNode }) => (
  <div className={`rounded-lg border border-divider bg-surface overflow-hidden ${className}`}>{children}</div>
)

const CardHeader = ({ width }: { width: string }) => (
  <div className="flex items-center h-11 px-4 border-b border-divider">
    <Skeleton className={`h-3.5 ${width}`} />
  </div>
)

/**
 * A Custom View page while it loads, in the shape of a dashboard: a title, a row of key numbers, a chart and a
 * table, on the page's own background, fading out down the view.
 */
export default function PageSkeleton({
  testId = 'custom-view-loading',
  label = 'Loading view',
  className = ''
}: {
  testId?: string
  label?: string
  className?: string
}) {
  return (
    <section
      className={`relative flex flex-1 min-h-0 overflow-hidden bg-surface-sunken ${className}`}
      data-testid={testId}
      role="status"
      aria-label={label}
    >
      {/* A scroll fade from the bottom up, as shadcn's: the page trails off rather than ending. */}
      <div
        aria-hidden
        className="flex w-full flex-col gap-4 p-6 max-sm:p-4 [mask-image:linear-gradient(to_bottom,black_55%,transparent_98%)]"
      >
        <div className="flex flex-col gap-2 py-1">
          <Skeleton className="h-5 w-56" />
          <Skeleton className="h-3.5 w-80 max-w-full" />
        </div>

        <div className="grid gap-4 grid-cols-[repeat(auto-fit,minmax(min(100%,220px),1fr))]">
          {KPI_LABELS.map((width, index) => (
            <Card key={width} className="flex flex-col gap-3 p-4">
              <Skeleton className={`h-3 ${width}`} />
              <Skeleton className={`h-6 ${KPI_VALUES[index]}`} />
            </Card>
          ))}
        </div>

        <Card>
          <CardHeader width="w-40" />
          <div className="flex items-end gap-3 h-56 p-4">
            {BAR_HEIGHTS.map((height, index) => (
              <Skeleton key={index} className="flex-1 rounded-b-none rounded-t" style={{ height }} />
            ))}
          </div>
        </Card>

        <Card className="shrink-0">
          <CardHeader width="w-36" />
          {Array.from({ length: TABLE_ROWS }, (_, row) => (
            <div key={row} className="flex items-center gap-8 h-9 px-3 border-b border-divider last:border-b-0">
              {TABLE_COLUMNS.map((width) => (
                <Skeleton key={width} className={`h-3 ${width}`} />
              ))}
            </div>
          ))}
        </Card>
      </div>
      <span className="sr-only">{label}</span>
    </section>
  )
}
